'use client'

import Link from 'next/link'
import { useEffect } from 'react'
import { X } from 'lucide-react'
import SafeImage from './SafeImage'
import { formatTryPrice, type CatalogProduct } from '@/data/catalog'

type AddToCartModalProps = {
  product: CatalogProduct
  qty?: number
  onClose: () => void
}

export default function AddToCartModal({
  product,
  qty = 1,
  onClose,
}: AddToCartModalProps) {
  const lineTotal = product.price * qty

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Sepete eklendi"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/45"
        aria-label="Kapat"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md bg-white shadow-2xl border border-gray-200 rounded-sm">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 left-3 w-9 h-9 flex items-center justify-center text-[#b91c1c] hover:bg-red-50"
          aria-label="Kapat"
        >
          <X size={20} />
        </button>

        <div className="px-5 pt-12 pb-5">
          <div className="flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-[#b45309] font-semibold text-base leading-snug">
                {product.name}
              </p>
              <p className="mt-2 text-sm text-[#111827]">
                {qty} × {formatTryPrice(product.price)}
              </p>
            </div>
            <div className="relative w-20 h-20 shrink-0 border border-gray-200 bg-gray-50 overflow-hidden">
              <SafeImage
                src={product.image}
                alt={product.name}
                fill
                sizes="80px"
                className="object-cover"
              />
            </div>
          </div>

          <p className="mt-5 text-sm font-semibold text-[#111827]">
            Ara toplam: {formatTryPrice(lineTotal)}
          </p>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <Link
              href="/sepet/"
              className="inline-flex items-center justify-center min-h-[44px] border border-[#f7941d] text-[#b45309] font-semibold text-sm hover:bg-orange-50"
              onClick={onClose}
            >
              Sepetim
            </Link>
            <Link
              href="/odeme/"
              className="inline-flex items-center justify-center min-h-[44px] border border-[#f7941d] text-[#b45309] font-semibold text-sm hover:bg-orange-50"
              onClick={onClose}
            >
              Ödeme
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
